import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { cashflowAPI, categoryAPI, expenseAPI, forecastAPI, balanceAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const CashflowContext = createContext(null);

export const CashflowProvider = ({ children }) => {
  const { user } = useAuth();
  const [cashflows, setCashflows] = useState([]);
  const [activeCashflow, setActiveCashflow] = useState(null);
  const [categories, setCategories] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(false);

  // Guards against stale responses when switching cashflows: each load captures
  // the cashflow id at request time and discards the response if the active
  // cashflow has changed by the time it resolves.
  const activeIdRef = useRef(null);

  const loadCashflows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cashflowAPI.getAll();
      setCashflows(response.data);
      
      // Set first cashflow as active if none selected
      if (response.data.length > 0) {
        setActiveCashflow(prev => prev || response.data[0]);
      }
    } catch (error) {
      console.error('Error loading cashflows:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    if (!activeCashflow) return;
    const requestId = activeCashflow.id;
    try {
      const response = await categoryAPI.getAll(requestId);
      if (activeIdRef.current !== requestId) return; // stale — cashflow switched
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  }, [activeCashflow]);

  const loadExpenses = useCallback(async () => {
    if (!activeCashflow) return;
    const requestId = activeCashflow.id;
    try {
      const response = await expenseAPI.getAll(requestId);
      if (activeIdRef.current !== requestId) return; // stale — cashflow switched
      setExpenses(response.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  }, [activeCashflow]);

  const loadForecast = useCallback(async (expenseType = null) => {
    if (!activeCashflow) return;
    const requestId = activeCashflow.id;
    try {
      const response = await forecastAPI.get(requestId, 24, expenseType);
      if (activeIdRef.current !== requestId) return; // stale — cashflow switched
      setForecast(response.data);
    } catch (error) {
      console.error('Error loading forecast:', error);
    }
  }, [activeCashflow]);

  // Load per-expense running balances. Runs the idempotent month-end close first
  // so any newly-ended unpaid months are accrued before we read the balances.
  const loadBalances = useCallback(async () => {
    if (!activeCashflow) return;
    const requestId = activeCashflow.id;
    try {
      try {
        await balanceAPI.closeMonths(requestId);
      } catch (closeErr) {
        console.error('Error closing months:', closeErr);
      }
      const response = await balanceAPI.getBalances(requestId);
      if (activeIdRef.current !== requestId) return; // stale — cashflow switched
      setBalances(response.data);
    } catch (error) {
      console.error('Error loading balances:', error);
    }
  }, [activeCashflow]);

  const recordBalanceEntry = useCallback(async (expenseId, entry) => {
    if (!activeCashflow) return;
    const response = await balanceAPI.addEntry(activeCashflow.id, expenseId, entry);
    await loadBalances();
    return response.data;
  }, [activeCashflow, loadBalances]);

  // Load cashflows when user changes
  useEffect(() => {
    if (user) {
      loadCashflows();
    } else {
      setCashflows([]);
      setActiveCashflow(null);
      setCategories([]);
      setExpenses([]);
      setForecast(null);
    }
  }, [user, loadCashflows]);

  // Load categories and expenses when active cashflow changes
  useEffect(() => {
    activeIdRef.current = activeCashflow?.id ?? null;
    if (activeCashflow) {
      loadCategories();
      loadExpenses();
      loadForecast();
      loadBalances();
    } else {
      setBalances(null);
    }
  }, [activeCashflow, loadCategories, loadExpenses, loadForecast, loadBalances]);

  const createCashflow = useCallback(async (data) => {
    const response = await cashflowAPI.create(data);
    await loadCashflows();
    return response.data;
  }, [loadCashflows]);

  const updateCashflow = useCallback(async (id, data) => {
    const response = await cashflowAPI.update(id, data);
    await loadCashflows();
    return response.data;
  }, [loadCashflows]);

  const deleteCashflow = useCallback(async (id) => {
    await cashflowAPI.delete(id);
    // Fetch the fresh list directly — picking a replacement from a stale
    // closure over `cashflows` could resurrect the deleted one.
    const response = await cashflowAPI.getAll();
    const fresh = response.data;
    setCashflows(fresh);
    setActiveCashflow(prev => (prev && prev.id !== id ? prev : fresh[0] || null));
  }, []);

  const createCategory = useCallback(async (data) => {
    if (!activeCashflow) return;
    const response = await categoryAPI.create({ ...data, cashflow_id: activeCashflow.id });
    await loadCategories();
    return response.data;
  }, [activeCashflow, loadCategories]);

  const deleteCategory = useCallback(async (id) => {
    await categoryAPI.delete(id);
    await loadCategories();
  }, [loadCategories]);

  const createExpense = useCallback(async (data) => {
    if (!activeCashflow) return;
    const response = await expenseAPI.create({ ...data, cashflow_id: activeCashflow.id });
    await loadExpenses();
    await loadForecast();
    return response.data;
  }, [activeCashflow, loadExpenses, loadForecast]);

  const updateExpense = useCallback(async (id, data) => {
    const response = await expenseAPI.update(id, data);
    await loadExpenses();
    await loadForecast();
    return response.data;
  }, [loadExpenses, loadForecast]);

  const deleteExpense = useCallback(async (id) => {
    await expenseAPI.delete(id);
    await loadExpenses();
    await loadForecast();
  }, [loadExpenses, loadForecast]);

  const markExpensePaid = useCallback(async (id, paidDate, amount, occurrenceDate = null) => {
    const payload = { paid_date: paidDate, amount };
    if (occurrenceDate) payload.occurrence_date = occurrenceDate;
    const response = await expenseAPI.markPaid(id, payload);
    await loadExpenses();
    await loadForecast();
    return response.data;
  }, [loadExpenses, loadForecast]);

  const unmarkExpensePaid = useCallback(async (id, occurrenceDate = null) => {
    await expenseAPI.unmarkPaid(id, occurrenceDate);
    await loadExpenses();
    await loadForecast();
  }, [loadExpenses, loadForecast]);

  const toggleExpense = useCallback(async (id) => {
    const response = await expenseAPI.toggle(id);
    await loadExpenses();
    await loadForecast();
    return response.data;
  }, [loadExpenses, loadForecast]);

  const refreshExpenses = useCallback(async () => {
    await loadExpenses();
    await loadForecast();
    await loadBalances();
  }, [loadExpenses, loadForecast, loadBalances]);

  const switchCashflow = useCallback((cashflow) => {
    setActiveCashflow(cashflow);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    cashflows,
    activeCashflow,
    categories,
    expenses,
    forecast,
    balances,
    loading,
    loadCashflows,
    loadCategories,
    loadExpenses,
    loadForecast,
    loadBalances,
    recordBalanceEntry,
    createCashflow,
    updateCashflow,
    deleteCashflow,
    createCategory,
    deleteCategory,
    createExpense,
    updateExpense,
    deleteExpense,
    markExpensePaid,
    unmarkExpensePaid,
    toggleExpense,
    refreshExpenses,
    switchCashflow,
  }), [
    cashflows,
    activeCashflow,
    categories,
    expenses,
    forecast,
    balances,
    loading,
    loadCashflows,
    loadCategories,
    loadExpenses,
    loadForecast,
    loadBalances,
    recordBalanceEntry,
    createCashflow,
    updateCashflow,
    deleteCashflow,
    createCategory,
    deleteCategory,
    createExpense,
    updateExpense,
    deleteExpense,
    markExpensePaid,
    unmarkExpensePaid,
    toggleExpense,
    refreshExpenses,
    switchCashflow,
  ]);

  return (
    <CashflowContext.Provider value={contextValue}>
      {children}
    </CashflowContext.Provider>
  );
};

export const useCashflow = () => {
  const context = useContext(CashflowContext);
  if (!context) {
    throw new Error('useCashflow must be used within a CashflowProvider');
  }
  return context;
};
